import * as React from "react";
import {useEffect, useMemo, useState} from "react";

import {useRenderState} from "@khanacademy/wonder-blocks-core";
import {AudioCache} from "@khan/guide-core-helpers";
import type {Voice} from "@khan/guide-types";

import {useGuidePreferences} from "../hooks/use-guide-preferences.ts";
import {isTextToSpeechSupported as isTextToSpeechSupportedFn} from "../utils/is-text-to-speech-supported.ts";

import {
    GuideAudioContext,
    type GuideAudioController,
} from "./guide-audio-context.ts";
import {
    loadAudioSample,
    useGuideAudioPlayback,
} from "./use-guide-audio-playback.ts";
import {useGuideAudioStream} from "./use-guide-audio-stream.ts";

interface Props {
    /**
     * Optional prop that can be used to provide an initial value for the audio
     * cache. The audio cache is a Map where the keys are interaction IDs and
     * the values are arrays of audio chunks.
     */
    initialAudioCache?: AudioCache;
    /**
     * Children to render inside the provider.
     */
    children: React.ReactNode;
}

/**
 * Provides an audio playback context to its children.
 */
export const GuideAudioProvider = (props: Props): React.ReactElement => {
    const {children, initialAudioCache} = props;
    const {
        preferences: {guideVoiceDescriptor, guideLanguageCode},
    } = useGuidePreferences();
    // We cache audio for interactions so that we can play it back at any
    // point during the conversation.
    const [audioCache, setAudioCache] = useState(
        initialAudioCache ?? new AudioCache(),
    );
    const [isVoiceOn, setIsVoiceOn] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState<Voice | null>(
        guideVoiceDescriptor,
    );
    const {
        audioKey,
        chunkIndex,
        isPlaying,
        pause,
        play,
        stop,
        outputVolumeAvg,
    } = useGuideAudioPlayback(audioCache);

    const isTextToSpeechSupported = useMemo(
        () => isTextToSpeechSupportedFn(guideLanguageCode),
        [guideLanguageCode],
    );

    useEffect(() => {
        if (isTextToSpeechSupported) {
            const errorSampleId = selectedVoice
                ? `${selectedVoice}:${guideLanguageCode}`
                : "lmnt:khanmigo-m-1:en";
            const key = `${errorSampleId}:error`;
            if (!audioCache.has(key)) {
                loadAudioSample(errorSampleId, "error").then((errorSample) => {
                    audioCache.set(key, [errorSample]);
                });
            }
        }
    }, [isTextToSpeechSupported, guideLanguageCode, selectedVoice, audioCache]);

    // If text-to-speech is not supported, stop any ongoing playback and turn
    // off the feature. This can happen if a user changes their preferred
    // language to one that we do not support.
    useEffect(() => {
        if (!isTextToSpeechSupported) {
            isPlaying && stop();
            setIsVoiceOn(false);
        }
        // We only want this effect to run when isTextToSpeechSupported changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting over from old code
    }, [isTextToSpeechSupported]);

    // Keep the selected voice in sync with the user's preferred voice.
    useEffect(() => {
        if (selectedVoice !== guideVoiceDescriptor) {
            setSelectedVoice(guideVoiceDescriptor);
        }
    }, [guideVoiceDescriptor, selectedVoice]);

    const context: GuideAudioController = useMemo(() => {
        return {
            audioCache,
            audioKey,
            chunkIndex,
            isTextToSpeechSupported,
            isPlaying,
            isVoiceOn,
            pause,
            play,
            selectedVoice,
            setAudioCache,
            setIsVoiceOn,
            setSelectedVoice,
            stop,
            outputVolumeAvg,
        };
    }, [
        audioCache,
        audioKey,
        chunkIndex,
        isTextToSpeechSupported,
        isPlaying,
        isVoiceOn,
        pause,
        play,
        selectedVoice,
        stop,
        outputVolumeAvg,
    ]);
    const renderState = useRenderState();

    return (
        <GuideAudioContext.Provider value={context}>
            {/* GuideAudioStream when added here causes some test failures
            due to the use of useLayoutEffect in SSR. To address this we
            don't render it while in SSR, which has been a solution in the
            past. */}
            {renderState === "standard" && <GuideAudioStream />}
            {children}
        </GuideAudioContext.Provider>
    );
};

const GuideAudioStream = (): null => {
    // Listen for streaming text-to-speech audio
    useGuideAudioStream();
    return null;
};
